import React, {useCallback, useState} from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Input,
    InputAdornment, Tooltip
} from '@deere/fuel-react';
import {Loading} from "../shared/Loading";
import {DataGrid} from '@mui/x-data-grid';
import {UserSearch} from "../shared/UserSearch";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import {Controller, useForm} from "react-hook-form";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import {Block} from "@material-ui/icons";
import {getOptionSelected} from "../shared/utils/autocompleteUtils";

export const TeamMembersCard = ({isLoading, supplierData, selectedStakeHolders, stakeholdersList, roleList, unitList,
    updateStakeholdersList, updateRacfIDEmpty, updateRoleEmpty, updateUnitEmpty, updateTeamMemberEmpty, isTeamMemberEmpty,
    updateErrorMsg, updateSelectedStakeholders, addDisable}) =>{
    const {control} = useForm();
    const [query, setQuery] = useState("");
    const [searchParam] = useState(["user", "role", "unit"]);
    const [teamMemberCount, setTeamMemberCount] = useState(0);
    const handleRowDelete = useCallback(
        (id) => {
            const updatedRows = stakeholdersList.map((row) => {
                if (row.id === id) {
                    row.status = "D"
                }
                return row;
            });
            updateStakeholdersList(updatedRows);
        },
        [stakeholdersList,updateStakeholdersList]
    );

    const handleRoleChange = useCallback(
        (value, id) => {
            updateRoleEmpty(false);
            updateErrorMsg("")
            const updatedRows = stakeholdersList.map((row) => {
                if (row.id === id) {
                    row.role = {...value}
                    row.status = "U"
                }
                return row;
            });
            updateStakeholdersList(updatedRows);
        },
        [stakeholdersList, updateStakeholdersList, updateErrorMsg, updateRoleEmpty]
    );

    const handleSelectedUser = useCallback(
        (value, id) => {
            updateRacfIDEmpty(false);
            updateErrorMsg("")
            const updatedRows = stakeholdersList.map((row) => {
                if (row.id === id) {
                    const descObj = {description: value ? `${value.lastName},${value.firstName}-${value.racfID}` : ""}
                    row.user = {...value, ...descObj};
                    row.status = "U"
                }
                return row;
            });
            updateStakeholdersList(updatedRows);
        },
        [stakeholdersList, updateStakeholdersList, updateErrorMsg, updateRacfIDEmpty]
    );

    const handleUnitChange = useCallback(
        (value, id) => {
            updateUnitEmpty(false);
            updateErrorMsg("")
            const updatedRows = stakeholdersList.map((row) => {
                if (row.id === id) {
                    row.unit = {...value}
                    row.status = "U"
                }
                return row;
            });
            updateStakeholdersList(updatedRows);
        },
        [stakeholdersList, updateStakeholdersList, updateUnitEmpty, updateErrorMsg]
    );

    const filteredRows = list => {
        const notDeletedList = list.filter((ele) => ele.status !== "D")
        return notDeletedList.filter((item) => {
            return searchParam.some((param) => {
                return item[param] && Object.keys(item[param]).length !== 0 && item[param]["description"].toString().toLowerCase().indexOf(query.toLowerCase()) > -1;
            })
        });
    };

    const handleStakeholderAdd = () => {
        updateTeamMemberEmpty(false);
        let obj = {
            role: "",
            unit: "",
            user: {
                description: "",
                firstName: "",
                lastName: "",
                racfID: ""
            },
            status: "A"
        }
        setTeamMemberCount(teamMemberCount + 1)
        const idObj = {id: teamMemberCount + 1}
        obj = {...idObj, ...obj};
        updateStakeholdersList([obj, ...stakeholdersList])
    }

    const handleRowSelection = (data, e) => {
        updateSelectedStakeholders(e)
    }

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            hide: true
        },
        {
            field: 'user',
            headerName: 'Stakeholder',
            flex: 1,
            valueGetter: (params) => params.row.user?.description || '',
            renderCell: (params) => {
                return (
                    <>
                        <UserSearch
                            defValue={params.row.user}
                            onOptionsChange={handleSelectedUser}
                            rowID={params.row.id}
                        />
                        {params.row?.userStatus && params.row?.userStatus !== "ACTIVE" && <Tooltip title={"Invalid User ID"}><Block htmlColor={"red"} /></Tooltip>}
                    </>
                )
            }
        },
        {
            field: 'role',
            headerName: 'Role',
            flex: 1,
            valueGetter: (params) => params.row.role?.description || '',
            renderCell: (params) => {
                return (
                    <Autocomplete
                        fullWidth
                        size="small"
                        id="role-find"
                        options={roleList}
                        defaultValue={params.row.role}
                        value={params.row.role}
                        getOptionLabel={(option) => option.description}
                        getOptionSelected={getOptionSelected}
                        onChange={(e, value) => {
                            handleRoleChange(value, params.row.id)
                        }}
                        renderInput={(params) => <TextField
                            {...params}
                            placeholder="Select Role"
                            variant="outlined"/>
                        }
                    />
                );
            }
        },
        {
            field: 'unit',
            headerName: 'Unit',
            flex: 1,
            valueGetter: (params) => params.row.unit?.description || '',
            renderCell: (params) => {
                return (
                    <Autocomplete
                        fullWidth
                        size="small"
                        id="unit-find"
                        options={unitList}
                        defaultValue={params.row.unit}
                        value={params.row.unit}
                        getOptionLabel={(option) => (Object.keys(option).length === 0) ? "" : `${option.description} - ${option.id}`}
                        getOptionSelected={getOptionSelected}
                        onChange={(e, value) => {
                            handleUnitChange(value, params.row.id)
                        }}
                        renderInput={(params) => <TextField
                            {...params}
                            placeholder="Select Unit"
                            variant="outlined"/>
                        }
                    />
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Action',
            renderCell: (params) => {
                return (
                    <IconButton aria-label="delete" edge="end"
                                onClick={() => handleRowDelete(params.row.id)}>
                        <DeleteIcon/>
                    </IconButton>
                )
            }
        }
    ];

    const rowsData = filteredRows(stakeholdersList) && filteredRows(stakeholdersList).map((item) => {
        return {
            id: item.id,
            user: item.user,
            role: item.role,
            unit: item.unit,
            userStatus: item.userStatus
        };
    });

    return(
        <Grid item xs={12}>
                                {isLoading ? (
                                    <Loading/>
                                ) : (
                                    <>
                                        {supplierData.functionalAreaType !== "" &&
                                        <Card>
                                            <CardContent>
                                                <Box mt={2}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={2}>
                                                            <FormControl fullWidth error>
                                                                <Box display="flex" justifyContent="flex-start">
                                                                    <Button variant="secondary"
                                                                            onClick={handleStakeholderAdd}
                                                                            disabled={addDisable}
                                                                            startIcon={<AddIcon/>}> Add
                                                                        Team
                                                                        Member</Button>
                                                                </Box>
                                                                <FormHelperText>{isTeamMemberEmpty && "Please Add Team members"}</FormHelperText>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <FormControl fullWidth>
                                                                <Input fullWidth placeholder="Search"
                                                                       id="search"
                                                                       endAdornment={
                                                                           <InputAdornment position="end">
                                                                               <IconButton aria-label="search">
                                                                                   <SearchIcon
                                                                                       style={{color: " #367c2b"}}/>
                                                                               </IconButton>
                                                                           </InputAdornment>
                                                                       }
                                                                       onChange={(e) => {
                                                                           setQuery(e.target.value)
                                                                       }}
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <FormControl required fullWidth>
                                                                <Controller
                                                                    rules={{required: true}}
                                                                    control={control}
                                                                    name="selectedStakeholders"
                                                                    defaultValue=""
                                                                    value={selectedStakeHolders}
                                                                    render={({onChange, value}) => (
                                                                        <DataGrid
                                                                            rows={rowsData}
                                                                            columns={columns}
                                                                            hideFooter
                                                                            checkboxSelection={false}
                                                                            disableSelectionOnClick
                                                                            autoHeight={true}
                                                                            onSelectionModelChange={(ids) => {
                                                                                const selectedIDs = new Set(ids);
                                                                                const selectedRowData = rowsData.filter((row) =>
                                                                                    selectedIDs.has(row.id)
                                                                                );
                                                                                onChange(selectedRowData, ids);
                                                                                handleRowSelection(selectedRowData, ids);
                                                                            }}
                                                                            selectionModel={(() => {
                                                                                return selectedStakeHolders;
                                                                            })()}
                                                                        />
                                                                    )}
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                        }
                                    </>
                                )}
                            </Grid>
    )
}
